import { uniqueId } from "lodash";
import { EnqueueSnackbar, SnackbarKey } from "notistack";
// Material UI
import { GridColDef, GridColumnTypes, GridFilterItem } from "@mui/x-data-grid-premium";
// Helpers
import { TWrapperFilterFunction, wrapFilterOperators, wrapQuickFilter } from "@/Common/helpers/mui/filters";
// Components
import { SnackbarsActions, SnackbarsContent } from "@/Layouts/components/Snackbars";
// Constants
import { positionFilterItemId } from "./constants";
// Types
import { IGetChangeRequest, IGetChangeRequestsByCommissionCycle } from "@/Washouts/types/PayrollClosing/queries";

// If the row is a skeleton row, it will be shown no matter what the filters are.
const checkForSkeleton: TWrapperFilterFunction<IGetChangeRequest> = (_value, row) =>
	row._id.startsWith("skeleton-row-") ? true : undefined;

const wrappedOperators = wrapFilterOperators(checkForSkeleton);
const wrappedQuickFilter = wrapQuickFilter(checkForSkeleton);

export const wrapColumnDefinitions = (columnDef: GridColDef<IGetChangeRequest>): GridColDef<IGetChangeRequest> => ({
	...columnDef,
	filterOperators: wrappedOperators[(columnDef.type as keyof GridColumnTypes) ?? "string"],
	getApplyQuickFilterFn: columnDef.type === "string" ? wrappedQuickFilter : undefined,
});

export const buildDataWithSkeletonRows = <T extends IGetChangeRequest>(
	data: T[],
	skeletonRows?: string[]
): (T & { __isShowingSkeleton: boolean })[] => {
	if (!skeletonRows) {
		return data.map((row) => ({ ...row, __isShowingSkeleton: false }));
	}

	const augmentedData = data.map((row) => ({ ...row, __isShowingSkeleton: skeletonRows?.includes(row._id) }));

	const newRows = skeletonRows?.filter((row) => !data.find((dataRow) => dataRow._id === row));
	if (!newRows?.length) {
		return augmentedData;
	}

	newRows.forEach(() => {
		augmentedData.push({ _id: uniqueId("skeleton-row-"), __isShowingSkeleton: true } as T & { __isShowingSkeleton: boolean });
	});

	return augmentedData;
};

export const changePositionFilterValue = (items: GridFilterItem[], newPositions: { label: string }[]) => {
	const newValue = newPositions.map((options) => options.label);

	return items.map((item) => {
		if (item.id === positionFilterItemId) {
			return { ...item, value: newValue };
		}
		return item;
	});
};

export const createErrorToast = (enqueueSnackbar: EnqueueSnackbar, message: string) =>
	enqueueSnackbar(<SnackbarsContent message={message} critical={true} />, {
		variant: "error",
		preventDuplicate: true,
		action: (key: SnackbarKey) => <SnackbarsActions key={key} index={key} />,
	});

export const canDelete = ({ row, selectedRowId, userId }: { row: IGetChangeRequest; selectedRowId: string | null; userId?: string }) => {
	return row.status === "PENDING" && row.createdBy._id === userId && row._id !== selectedRowId;
};

export const changeRequestSetQueryDataCallback = (
	action: string,
	oldData: IGetChangeRequestsByCommissionCycle,
	newData: { updateChangeRequest: IGetChangeRequestsByCommissionCycle }
) => {
	const actionsMap: { [x: string]: () => void } = {
		DELETE: () => {
			return {
				...oldData,
				requests: oldData?.requests?.filter((request) => request?._id !== newData?.updateChangeRequest?.requests?.[0]?._id),
			};
		},
		UPDATE_STATUS: () => {
			return {
				...oldData,
				requests: oldData?.requests?.map((request) => {
					if (request?._id === newData?.updateChangeRequest?.requests?.[0]?._id) {
						return {
							...request,
							status: newData?.updateChangeRequest?.requests?.[0]?.status,
						};
					}
					return request;
				}),
			};
		},
		UPDATE_SUPPORT_ASSIGNED: () => {
			return {
				...oldData,
				requests: oldData?.requests?.map((request) => {
					if (request?._id === newData?.updateChangeRequest?.requests?.[0]?._id) {
						return {
							...request,
							supportAssigned: newData?.updateChangeRequest?.requests?.[0]?.supportAssigned,
						};
					}
					return request;
				}),
			};
		},
		SEND_MESSAGE: () => {
			return {
				...oldData,
				requests: oldData?.requests?.map((request) => {
					if (request?._id === newData?.updateChangeRequest?.requests?.[0]?._id) {
						return {
							...request,
							chat: newData?.updateChangeRequest?.requests?.[0]?.chat,
						};
					}
					return request;
				}),
			};
		},
	};
	return actionsMap[action]?.();
};
